import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatEastern, loadSiteInfo } from "../../lib/siteInfo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { LaunchCard, StatusBadge, inputClass, inputStyle, toLocalInput } from "./ui";

function GoLiveNowDialog({ open, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/golive/now", { confirm, note });
      toast.success(`Production is live; ${data.invites_sent} queued invitation(s) sent`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="golive-now-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Go live now</DialogTitle>
          <DialogDescription className="text-sm">
            Real email starts, employer users can sign in and every queued invitation is sent. This cannot be undone from here.
          </DialogDescription>
        </DialogHeader>
        <label className="field-label">Why now? (kept in the audit trail)</label>
        <input className={inputClass} style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} data-testid="golive-now-note" />
        <label className="field-label mt-3">Type GO LIVE to confirm</label>
        <input className={inputClass} style={inputStyle} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="golive-now-confirm-input" />
        <div className="mt-4 flex justify-end gap-3">
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="golive-now-cancel">Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || confirm.trim().toUpperCase() !== "GO LIVE" || note.trim().length < 3} onClick={submit} data-testid="golive-now-submit">
            {busy ? "Going live…" : "Go live now"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleForm({ current, disabled, onSaved }) {
  const [when, setWhen] = useState(toLocalInput(current));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setWhen(toLocalInput(current)); }, [current]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/golive", { go_live_at: new Date(when).toISOString(), note });
      toast.success("Go-live time updated");
      setNote("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-[220px_1fr_auto] items-end mt-5">
      <div>
        <label className="field-label">New go-live time (your local time)</label>
        <input type="datetime-local" className={inputClass} style={inputStyle} value={when} onChange={(e) => setWhen(e.target.value)} disabled={disabled} data-testid="golive-datetime-input" />
      </div>
      <div>
        <label className="field-label">Reason (audit note)</label>
        <input className={inputClass} style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} disabled={disabled} placeholder="e.g. DNS change moved by the registrar" data-testid="golive-note-input" />
      </div>
      <button type="button" className="btn-ghost" disabled={disabled || busy || !when || note.trim().length < 3} onClick={save} data-testid="golive-postpone-btn">
        {busy ? "Saving…" : "Reschedule"}
      </button>
    </div>
  );
}

export function GoLiveCard() {
  const [data, setData] = useState(null);
  const [askNow, setAskNow] = useState(false);
  const load = useCallback(() => api.get("/admin/golive").then(({ data: d }) => setData(d))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const live = data.status === "live";
  const refresh = () => { setAskNow(false); load(); loadSiteInfo(true); };
  return (
    <LaunchCard eyebrow={`Go live · ${data.environment}`} title="Scheduled go-live" testId="golive-card"
      aside={<StatusBadge status={live ? "pass" : "warn"} label={live ? "Live" : data.applies ? "Pre-launch" : "Not applicable here"} testId="golive-status-badge" />}>
      {!data.applies && (
        <p className="text-sm mb-3" style={{ color: "var(--muted)" }} data-testid="golive-dev-note">
          This is dev-portal: it never has a pre-launch period because all email already goes to the contained address. The schedule below matters on the production site.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-3 text-sm">
        <div><div className="eyebrow text-[9px]">{live ? "Went live" : "Goes live"}</div>
          <div className="font-semibold mt-1" style={{ color: "var(--ink)" }} data-testid="golive-time">{formatEastern(live ? data.went_live_at : data.go_live_at)}</div></div>
        <div><div className="eyebrow text-[9px]">Outgoing email</div>
          <div className="mt-1" style={{ color: "var(--ink)" }} data-testid="golive-email-state">{data.prelaunch ? `Held — sent to ${data.email_hold_to}` : live ? "Real email" : "Contained (dev)"}</div></div>
        <div><div className="eyebrow text-[9px]">Queued invitations</div>
          <div className="mt-1 font-semibold" style={{ color: "var(--ink)" }} data-testid="golive-queued-count">{data.queued_invites}</div></div>
      </div>
      {!live && <ScheduleForm current={data.go_live_at} disabled={live} onSaved={refresh} />}
      {!live && data.applies && (
        <button type="button" className="btn-primary mt-4" onClick={() => setAskNow(true)} data-testid="golive-now-btn">Go live now…</button>
      )}
      {Boolean(data.history.length) && (
        <ul className="mt-5 border-t pt-3 text-xs space-y-1" style={{ borderColor: "var(--border)", color: "var(--muted)" }} data-testid="golive-history">
          {data.history.map((h) => (
            <li key={`${h.at}-${h.to}`}>{new Date(h.at).toLocaleString("en-US")} · {h.by}: {h.to === "now" ? "went live now" : `→ ${formatEastern(h.to)}`} — {h.note}</li>
          ))}
        </ul>
      )}
      <GoLiveNowDialog open={askNow} onClose={() => setAskNow(false)} onDone={refresh} />
    </LaunchCard>
  );
}
